// /clubs: mirrors screens/HomeScreen.js (feed, categories, search, Club Life, Apply flow).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Band, Page } from "../components/Band";
import Icon from "../components/Icon";
import { Button, Chip, Content, Empty, FilterChip, SkeletonRows } from "../components/primitives";
import { ClubService } from "../../../services/clubService";
import useClubSearchTracking from "../../../hooks/useClubSearchTracking";
import useClubLifeTiles from "../../../components/home/useClubLifeTiles";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import useClubApply from "../hooks/useClubApply";
import useClubsFeed from "../components/clubs/useClubsFeed";
import useCategories from "../components/clubs/useCategories";
import ClubCard from "../components/clubs/ClubCard";
import ClubLifeCarousel from "../components/clubs/ClubLifeCarousel";
import CategorySheet from "../components/clubs/CategorySheet";
import { categoryEmoji } from "../../../utils/categoryEmoji";
import { joinStateFor } from "../components/clubs/clubUtils";
import "../styles/clubs-web.css";

const PAGE_SIZE = 20;

export default function ClubsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userClubs, loadUserClubs } = useData();
  const { clubs, loading, error, reload } = useClubsFeed();
  const { categories } = useCategories();

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(null); // category object or null = All
  const [sheet, setSheet] = useState(false);
  const [shown, setShown] = useState(PAGE_SIZE);
  const [applications, setApplications] = useState([]);
  const searchRef = useRef(null);
  const appsEpoch = useRef(0);

  // Pending applications (HomeScreen.js:371); re-read on mount and after every apply attempt.
  const fetchApplications = useCallback(async () => {
    if (!user) return;
    const epoch = ++appsEpoch.current;
    const { data, error: e } = await ClubService.getUserApplications(user.uid);
    if (epoch !== appsEpoch.current || e) return;
    setApplications(data || []);
  }, [user]);
  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const refresh = useCallback(() => { loadUserClubs(); fetchApplications(); }, [loadUserClubs, fetchApplications]);
  const { applyingClubId, applyBursts, onJoinPress } = useClubApply({ user, refresh });

  // "/" focuses the search pill (unless typing somewhere already).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t?.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t?.tagName || "")) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const memberIds = useMemo(() => new Set((userClubs || []).map((c) => c.id)), [userClubs]);
  const pendingIds = useMemo(
    () => new Set(applications.filter((a) => a.status === "pending").map((a) => a.clubId)),
    [applications]
  );

  // Client-side search + category filter + member_count sort (HomeScreen.js:226-247).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...clubs];
    if (q) list = list.filter((c) => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
    if (category) list = list.filter((c) => c.category === category.name || c.category_id === category.id);
    return list.sort((a, b) => (b.member_count || 0) - (a.member_count || 0));
  }, [clubs, query, category]);

  useClubSearchTracking(query, filtered);
  useEffect(() => { setShown(PAGE_SIZE); }, [query, category]);

  const tiles = useClubLifeTiles(clubs);
  const open = (club) => navigate(`/clubs/${club.id}`, { state: { club, source: "home_list" } });
  const visible = filtered.slice(0, shown);
  const showLife = !query.trim() && !category && tiles.length > 0;

  return (
    <Page>
      <Band title="Clubs" sub="Explore student organizations at UM" />
      <Content>
        <div className="search">
          <Icon name="search" size={20} />
          <input
            ref={searchRef}
            placeholder="Search clubs"
            aria-label="Search clubs"
            data-testid="home.search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="kbd hide-sm" aria-hidden="true">/</span>
          <Chip iconOnly icon="filter" style={{ boxShadow: "none", background: "var(--blue50)" }} aria-label="Filter by category" data-testid="home.filter" onClick={() => setSheet(true)} />
        </div>

        {showLife && (
          <>
            <div className="section-h"><h2>Club Life</h2><span className="meta">Swipe through campus</span></div>
            <ClubLifeCarousel tiles={tiles} onOpen={open} />
          </>
        )}

        <div className="section-h"><h2>Explore Clubs</h2><span className="meta">{loading ? "" : `${filtered.length} clubs`}</span></div>
        <div className="chips" style={{ marginBottom: 14 }}>
          <FilterChip on={!category} data-testid="home.category.all" onClick={() => setCategory(null)}>All</FilterChip>
          {categories.map((c) => (
            <FilterChip key={c.id} on={category?.id === c.id} data-testid={`home.category.${c.id}`} onClick={() => setCategory(c)}>
              {categoryEmoji(c.name) ? `${categoryEmoji(c.name)} ` : ""}{c.name}
            </FilterChip>
          ))}
        </div>

        {loading ? (
          <SkeletonRows rows={6} />
        ) : error ? (
          <Empty icon="alert" title="Couldn't load clubs">
            <p className="meta" style={{ marginBottom: 12 }}>{error}</p>
            <Button onClick={reload} data-testid="home.retry">Retry</Button>
          </Empty>
        ) : (
          <>
            <div className="clubgrid">
              {visible.map((c) => {
                const state = joinStateFor(c, memberIds, pendingIds);
                return (
                  <ClubCard
                    key={c.id}
                    club={c}
                    state={state}
                    busy={applyingClubId === c.id}
                    burst={applyBursts[c.id] || 0}
                    onOpen={open}
                    onJoin={onJoinPress}
                  />
                );
              })}
            </div>
            {filtered.length === 0 && <div className="empty card">No clubs match that search.</div>}
            {filtered.length > shown && (
              <div className="row" style={{ justifyContent: "center", marginTop: 18 }}>
                <Button variant="sec" onClick={() => setShown((n) => n + PAGE_SIZE)} data-testid="home.loadMore">Load more</Button>
              </div>
            )}
          </>
        )}
      </Content>

      <button type="button" className="fab" data-testid="home.createClub" onClick={() => navigate("/clubs/new")}>
        <Icon name="plus" size={20} />Create Club
      </button>

      {sheet && (
        <CategorySheet
          categories={categories}
          selected={category}
          onPick={(c) => { setCategory(c); setSheet(false); }}
          onClose={() => setSheet(false)}
        />
      )}
    </Page>
  );
}
